<template>
    <div class="addressEntry">
        <header>
            <div class="cn"><h1>{{dispEntry.cn}}</h1></div>
            <div class="closebutton">
                <icon-button :icon="['far', 'window-close']" size="2x" @click="$emit('hide')"></icon-button>
            </div>
        </header>
        <nav>
            <b-button-toolbar key-nav>
                <b-button size="sm" variant="primary" class="mr-3" @click="doSendTo" v-if="entry.mail">
                    <font-awesome-icon icon="paper-plane"></font-awesome-icon>
                    {{$t('nav.sendTo')}}
                </b-button>
                <b-button size="sm" variant="secondary" class="mr-3" @click="doCopy" v-if="entry.mail">
                    <font-awesome-icon icon="copy"></font-awesome-icon>
                    {{$t('nav.copyEmail')}}
                </b-button>
                <b-button size="sm" variant="secondary" class="mr-3" v-if="(entryBook.rights >= 5) && !editMode"
                        @click="doEdit">
                    <font-awesome-icon icon="edit"></font-awesome-icon>
                    {{$t('nav.edit')}}
                </b-button>
                <b-button size="sm" variant="secondary" class="mr-3" v-if="(entryBook.rights >= 5) && !editMode"
                        @click="doDelete">
                    <font-awesome-icon icon="trash-alt"></font-awesome-icon>
                    {{$t('nav.delete')}}
                </b-button>
                <b-button size="sm" variant="secondary" class="mr-3" v-if="editMode" @click="doSave">
                    <font-awesome-icon icon="save"></font-awesome-icon>
                    {{$t('nav.save')}}
                </b-button>
                <b-button size="sm" variant="secondary" class="mr-3" v-if="editMode" @click="cancelEdit">
                    <font-awesome-icon icon="times"></font-awesome-icon>
                    {{$t('nav.cancel')}}
                </b-button>
            </b-button-toolbar>
        </nav>
        <main>
            <b-container>
                <b-row>
                    <b-col>{{$t('cn')}}</b-col>
                    <b-col><switchable-input v-model="dispEntry.cn" :edit="editMode"/></b-col>
                </b-row>
                <b-row>
                    <b-col>{{$t('gn')}}</b-col>
                    <b-col><switchable-input :value="dispEntry.gn" :edit="editMode"
                            @input="updateCN('gn', $event)"/></b-col>
                </b-row>
                <b-row>
                    <b-col>{{$t('initials')}}</b-col>
                    <b-col><switchable-input :value="dispEntry.initials" :edit="editMode"
                            @input="updateCN('initials', $event)"/></b-col>
                </b-row>
                <b-row>
                    <b-col>{{$t('sn')}}</b-col>
                    <b-col><switchable-input :value="dispEntry.sn" :edit="editMode"
                            @input="updateCN('sn', $event)"/></b-col>
                </b-row>
                <b-row>
                    <b-col>{{$t('mail')}}</b-col>
                    <b-col><switchable-input v-model="dispEntry.mail" :edit="editMode"/></b-col>
                </b-row>

                <b-row v-for="f in editMode ? optionalFields : existingOptionalFields" :key="f.id">
                    <template v-if="f.header">
                        <b-col><h2>{{$t(f.id)}}</h2></b-col>
                    </template>
                    <template v-else>
                        <b-col>{{$t(f.id)}}</b-col>
                        <b-col><switchable-input v-model="dispEntry[f.id]" :edit="editMode"
                                :type="f.type ? f.type : 'input'" :placeholder="$t(`placeholder.${f.id}`)"/></b-col>
                    </template>
                </b-row>

                <b-row class="bookname">
                    <b-col>{{$t('book')}}</b-col>
                    <b-col>{{entryBook.displayName}}</b-col>
                </b-row>
            </b-container>
        </main>
    </div>
</template>

<script>
import IconButton from './iconButton';
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import { EventBus } from '@/event-bus';
import SwitchableInput from '@/components/switchableInput';

export default {
  name: 'addressEntryView',
  components: { IconButton, FontAwesomeIcon, SwitchableInput },
  i18nOptions: { keyPrefix: 'addrEntry' },
  data: function() {
    return {
      editMode: false,
      editEntry: {},
      optionalFields: [
        { id: 'telephone', header: true },
        { id: 'telephonenumber' },
        { id: 'homephone' },
        { id: 'mobile' },
        { id: 'fax' },
        { id: 'businessinfo', header: true },
        { id: 'title' },
        { id: 'physicaldeliveryofficename' },
        { id: 'ou' },
        { id: 'o' },
        { id: 'postaladdress', type: 'textbox' },
        { id: 'l' },
        { id: 'st' },
        { id: 'postalcode' },
        { id: 'c' },
        { id: 'personalinfo', header: true },
        { id: 'homepostaladdress', type: 'textbox' },
        { id: 'info' },
        { id: 'url' }
      ]
    };
  },
  props: {
    dn: String,
    entry: Object,
    scopes: Array
  },
  methods: {
    doCopy() {
      if (this.entry.mail) {
        if (this.entry.cn) {
          navigator.clipboard.writeText(`"${this.entry.cn}" <${this.entry.mail}>`);
        } else {
          navigator.clipboard.writeText(this.entry.mail);
        }
      }
    },
    doSendTo() {
      EventBus.$emit('compose', {
        rcptsTo: [{ name: this.entry.cn, addr: this.entry.mail }],
        subject: '',
        content: ''
      });
    },
    doEdit() {
      this.editMode = true;
      this.editEntry = Object.assign({}, this.entry);
    },
    cancelEdit() {
      this.editMode = false;
    },
    doSave() {
      let saveEntry = Object.assign({}, this.editEntry);
      saveEntry.displayName = saveEntry.cn;
      this.$store.dispatch('updateAddrBookEntry', { dn: this.dn, entry: saveEntry })
        .then(() => {
          this.$emit('hide');
          this.cancelEdit();
        })
        .catch(e => {
          this.$dialog.alert(this.$t('saveError', { error: e.message }));
        });
    },
    doDelete() {
      this.$store.dispatch('deleteAddrBookEntry', { dn: this.dn })
        .then(() => {
          this.$emit('hide');
        })
        .catch(e => {
          this.$dialog.alert(this.$t('deleteError', { error: e.message }));
        });
    },
    updateCN(field, val) {
      console.log('updateCN', field, val);
      let CNParts = this.editEntry.cn.split(/\s+/);
      let buildType = '';
      if (CNParts.length === 1) {
        if (CNParts[0] === this.editEntry['gn']) {
          buildType = 'gn';
        } else if (CNParts[0] === this.editEntry['sn']) {
          buildType = 'sn';
        }
      } else if (CNParts.length === 2) {
        if ((CNParts[0] === this.editEntry['gn']) && (CNParts[1] === this.editEntry['sn'])) {
          buildType = 'gn sn';
        } else if ((CNParts[0] === this.editEntry['sn']) && (CNParts[1] === this.editEntry['gn'])) {
          buildType = 'sn gn';
        }
      } else if (CNParts.length === 3) {
        if ((CNParts[0] === this.editEntry['gn']) && (CNParts[1] === this.editEntry['initials']) &&
          (CNParts[2] === this.editEntry['sn'])) {
          buildType = 'gn initials sn';
        }
      }
      this.editEntry[field] = val;

      if (buildType) {
        CNParts = buildType.split(' ');
        this.editEntry.cn = CNParts.map(p => this.editEntry[p]).join(' ');
      }
    }
  },
  computed: {
    entryBook() {
      return this.scopes.find(s => s.id === this.entry._parentid);
    },
    dispEntry() {
      return this.editMode ? this.editEntry : this.entry;
    },
    existingOptionalFields() {
      return this.optionalFields
        .filter(f => {
          return f.header || this.entry[f.id];
        })
        .filter((f, ind, arr) => {
          if (f.header) {
            if ((ind === arr.length - 1) || (arr[ind + 1].header)) {
              return false;
            }
          }
          return true;
        });
    }
  }
};
</script>

<style scoped lang="scss">
    @import '../assets/bootstrap-style.scss';

    .addressEntry {
        display: grid;
        grid-template-rows: auto auto 1fr;
        height: 100%;
        header {
            display: flex;
            background-color: $address-entry-header-bg;
            color: $address-entry-header-fg;
            padding: 0 10px;
            .cn {
                flex: 1;
            }
            .closebutton {
                flex: 0 0 auto;
                margin: 10px 10px 0 0;
                .ib_wrap {
                    color: $gray-300 !important;
                    transition: all 0.5s;
                    &:hover {
                        color: $light !important;
                        background-color: inherit !important;
                        font-size: 120%;
                    }
                }
            }
        }
        nav {
            height: 38px;
            background-color: $address-entry-nav-bg;
            padding: 2px 10px 0 10px;
            color: $address-entry-nav-fg;
        }

        main {
            overflow-y: auto;
            h2 {
                font-size: 1.2em;
                font-weight: bold;
                text-decoration: wavy;
                margin: 5px 0 0 0;
            }

            .bookname {
                margin-top: 0.5em;
                color: $gray-500;
                font-style: italic;
            }
        }
    }
</style>
